import type {
  GuidedWorldcupDraft,
  WorldcupResearchSearch,
} from '~/features/research-workspace/worldcup-search'
import type {
  WorldcupResearchQuery,
  WorldcupResearchTeamMatchStatRow,
} from '~/features/worldcup-research/types'
import type { ImplementedLocale } from '~/lib/i18n/shared'
import { Button, Card } from '@heroui/react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import * as React from 'react'
import { useLocale, useTranslations } from 'use-intl'
import {
  publicResearchFacetsQueryOptions,
  publicResearchTeamMatchStatsQueryOptions,
} from '~/features/public-research/query-options'
import {
  cleanWorldcupSearch,
  draftFromGuidedFilters,
  researchViewForCompetitions,
  worldcupResearchDefaultFromYear,
  worldcupResearchSearchSchema,
} from '~/features/research-workspace/worldcup-search'
import { formatCompetitionName } from '~/lib/i18n/messages/domain/competitions'
import { formatTeamName } from '~/lib/i18n/messages/domain/teams'
import {
  AppliedFilters,
  formatProviderName,
  formatValue,
  matchDetailPath,
  metricShare,
  PageHeader,
  SelectInput,
  uniqueValues,
} from './components'
import { classNames, includeSelectedOption } from './utils'

interface MatchStatGroup {
  key: string
  rows: WorldcupResearchTeamMatchStatRow[]
}

function groupMatchStatRows(rows: WorldcupResearchTeamMatchStatRow[]): MatchStatGroup[] {
  const groups = new Map<string, WorldcupResearchTeamMatchStatRow[]>()
  for (const row of rows) {
    const fallbackTeams = [row.team, row.opponent].sort().join(':')
    const key = row.matchId || `${row.competitionCode}:${row.competitionName}:${row.season}:${row.stage ?? ''}:${fallbackTeams}`
    const existing = groups.get(key)
    if (existing)
      existing.push(row)
    else
      groups.set(key, [row])
  }
  return Array.from(groups.entries()).map(([key, groupedRows]) => ({ key, rows: groupedRows }))
}

export function GuidedWorldcupResearch({
  search = worldcupResearchSearchSchema.parse({}),
  onSearchChange,
}: {
  search?: WorldcupResearchSearch
  onSearchChange?: (search: Record<string, string | number>, options?: { replace?: boolean }) => void
} = {}) {
  const t = useTranslations('ResearchWorkspace.worldcup')
  const locale = useLocale() as ImplementedLocale
  const searchKey = JSON.stringify(search)
  const [filters, setFilters] = React.useState<WorldcupResearchQuery>(() => search)
  const [draft, setDraft] = React.useState<GuidedWorldcupDraft>(() => draftFromGuidedFilters(search))
  const [shouldScrollToResults, setShouldScrollToResults] = React.useState(false)
  const resultsRef = React.useRef<HTMLDivElement>(null)
  const statsFilters = React.useMemo<WorldcupResearchQuery>(() => ({ ...filters, includeComparisonRows: true }), [filters])
  const statsQuery = useQuery(publicResearchTeamMatchStatsQueryOptions(statsFilters))
  const selectedCompetitionCodes = draft.competitionCodes.length > 0 ? draft.competitionCodes : ['world_cup']
  const draftResearchView = researchViewForCompetitions(selectedCompetitionCodes as WorldcupResearchQuery['competitionCodes'])
  const facetsQuery = useQuery(publicResearchFacetsQueryOptions({
    competitionCodes: selectedCompetitionCodes as WorldcupResearchQuery['competitionCodes'],
    competitionName: draft.competitionName.trim() || undefined,
    researchView: draftResearchView,
    minDataQualityScore: filters.minDataQualityScore,
  }))
  const competitionOptions = [
    { value: 'world_cup', label: t('competitionOptions.worldCup') },
    { value: 'euro', label: t('competitionOptions.euro') },
    { value: 'copa_america', label: t('competitionOptions.copaAmerica') },
    { value: 'nations_league', label: t('competitionOptions.nationsLeague') },
    { value: 'world_cup_qualifier', label: t('competitionOptions.worldCupQualifier') },
    { value: 'friendlies', label: t('competitionOptions.friendlies') },
  ]
  const teamOptions = includeSelectedOption([
    { value: '', label: t('allTeams') },
    ...(facetsQuery.data?.facets.teams ?? []).map((item: any) => ({ value: item.value, label: formatTeamName(item.value, locale) })),
  ], draft.team, formatTeamName(draft.team, locale))
  const opponentOptions = includeSelectedOption([
    { value: '', label: t('allOpponents') },
    ...(facetsQuery.data?.facets.opponents ?? []).map((item: any) => ({ value: item.value, label: formatTeamName(item.value, locale) })),
  ], draft.opponent, formatTeamName(draft.opponent, locale))
  const seasonOptions = includeSelectedOption([
    { value: '', label: t('allSeasons') },
    ...(facetsQuery.data?.facets.seasons ?? []).map((item: any) => ({ value: item.value, label: item.label || item.value })),
  ], draft.season)
  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: currentYear - 1990 + 1 }, (_, index) => {
    const year = String(1990 + index)
    return { value: year, label: year }
  })
  const fromYearOptions = includeSelectedOption([
    { value: '', label: t('allSeasons') },
    ...yearOptions,
  ], draft.fromYear)
  const toYearOptions = includeSelectedOption([
    { value: '', label: t('presentYear') },
    ...yearOptions,
  ], draft.toYear)
  const isSingleCompetition = selectedCompetitionCodes.length === 1
  const matchGroups = groupMatchStatRows(statsQuery.data?.rows ?? [])
  const totalRows = statsQuery.data?.totalRows ?? statsQuery.data?.rows.length ?? 0
  const returnedPrimaryRows = (statsQuery.data?.rows ?? []).filter(row => !row.comparisonOnly).length
  const currentOffset = filters.offset ?? 0
  const currentLimit = filters.limit ?? 25
  const resultStart = totalRows > 0 ? currentOffset + 1 : 0
  const resultEnd = currentOffset + returnedPrimaryRows
  const canGoPrev = currentOffset > 0
  const canGoNext = resultEnd < totalRows

  React.useEffect(() => {
    setFilters(search)
    setDraft(draftFromGuidedFilters(search))
  }, [searchKey])

  function syncSearchParams(nextFilters: WorldcupResearchQuery, replace = false) {
    onSearchChange?.(cleanWorldcupSearch(nextFilters), { replace })
  }

  function updateFilters(nextFilters: WorldcupResearchQuery, options: { replace?: boolean, scroll?: boolean } = {}) {
    setFilters(nextFilters)
    syncSearchParams(nextFilters, options.replace)
    if (options.scroll) {
      setShouldScrollToResults(true)
    }
  }

  React.useEffect(() => {
    if (!shouldScrollToResults || statsQuery.isPending)
      return
    resultsRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    setShouldScrollToResults(false)
  }, [shouldScrollToResults, statsQuery.isPending, statsQuery.data])

  function applyGuidedQuery(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const competitionCodes = (draft.competitionCodes.length > 0 ? draft.competitionCodes : ['world_cup']) as WorldcupResearchQuery['competitionCodes']
    const competitionCode = competitionCodes?.[0] as WorldcupResearchQuery['competitionCode']
    const researchView = researchViewForCompetitions(competitionCodes)
    const fromYear = draft.fromYear ? Number(draft.fromYear) : undefined
    const rawToYear = draft.toYear ? Number(draft.toYear) : undefined
    const toYear = fromYear && rawToYear && rawToYear < fromYear ? undefined : rawToYear
    updateFilters({
      ...filters,
      competitionCode,
      competitionCodes,
      researchView,
      team: draft.team.trim() || undefined,
      opponent: draft.opponent.trim() || undefined,
      competitionName: draft.competitionName.trim() || undefined,
      season: isSingleCompetition && draft.season ? Number(draft.season) as WorldcupResearchQuery['season'] : undefined,
      fromYear,
      toYear,
      offset: 0,
    }, { scroll: true })
  }

  function goToResultOffset(offset: number) {
    updateFilters({
      ...filters,
      offset: Math.max(0, offset),
    }, { scroll: true })
  }

  function toggleCompetitionCode(competitionCode: string) {
    setDraft((current) => {
      const hasCode = current.competitionCodes.includes(competitionCode)
      const nextCodes = hasCode
        ? current.competitionCodes.filter(code => code !== competitionCode)
        : [...current.competitionCodes, competitionCode]
      return {
        ...current,
        competitionCodes: nextCodes.length > 0 ? nextCodes : [competitionCode],
        season: '',
      }
    })
  }

  function applyYearShortcut(shortcut: 'recent5' | 'recent10' | 'since2010' | 'all') {
    const nextRange = {
      recent5: { fromYear: String(currentYear - 4), toYear: String(currentYear) },
      recent10: { fromYear: String(currentYear - 9), toYear: String(currentYear) },
      since2010: { fromYear: String(worldcupResearchDefaultFromYear), toYear: '' },
      all: { fromYear: '', toYear: '' },
    }[shortcut]
    setDraft(current => ({ ...current, ...nextRange, season: '' }))
  }

  return (
    <div className="space-y-5">
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} subtitle={t('subtitle')} />
      <Card className="p-5" variant="secondary">
        <form className="space-y-4" onSubmit={applyGuidedQuery}>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(140px,auto)]">
            <SelectInput label={t('teamLabel')} value={draft.team} options={teamOptions} onChange={team => setDraft(current => ({ ...current, team }))} />
            <SelectInput label={t('opponentLabel')} value={draft.opponent} options={opponentOptions} onChange={opponent => setDraft(current => ({ ...current, opponent }))} />
            <div className="flex min-w-0 items-end">
              <Button type="submit" className="w-full" variant="primary">{t('search')}</Button>
            </div>
          </div>
          <fieldset className="min-w-0 lg:col-span-2">
            <legend className="mb-1 text-sm font-semibold text-muted">{t('competitionCodeLabel')}</legend>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {competitionOptions.map(option => (
                <label
                  key={option.value}
                  className={classNames(
                    'flex min-h-10 items-center gap-2 rounded-md border px-3 text-sm font-semibold transition',
                    selectedCompetitionCodes.includes(option.value)
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-divider bg-background text-muted',
                  )}
                >
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={selectedCompetitionCodes.includes(option.value)}
                    onChange={() => toggleCompetitionCode(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="mb-1 text-sm font-semibold text-muted">{t('timeRangeLabel')}</legend>
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,180px)_minmax(0,180px)_1fr]">
              <SelectInput label={t('fromYearLabel')} value={draft.fromYear} options={fromYearOptions} onChange={fromYear => setDraft(current => ({ ...current, fromYear, season: '' }))} />
              <SelectInput label={t('toYearLabel')} value={draft.toYear} options={toYearOptions} onChange={toYear => setDraft(current => ({ ...current, toYear, season: '' }))} />
              <div className="flex flex-wrap items-end gap-2">
                <Button type="button" size="sm" variant="outline" onPress={() => applyYearShortcut('recent5')}>{t('yearShortcuts.recent5')}</Button>
                <Button type="button" size="sm" variant="outline" onPress={() => applyYearShortcut('recent10')}>{t('yearShortcuts.recent10')}</Button>
                <Button type="button" size="sm" variant="outline" onPress={() => applyYearShortcut('since2010')}>{t('yearShortcuts.since2010')}</Button>
                <Button type="button" size="sm" variant="outline" onPress={() => applyYearShortcut('all')}>{t('yearShortcuts.all')}</Button>
              </div>
            </div>
          </fieldset>
          {isSingleCompetition
            ? (
                <div className="grid gap-2 md:grid-cols-[minmax(0,240px)_1fr]">
                  <SelectInput label={t('editionLabel')} value={draft.season} options={seasonOptions} onChange={season => setDraft(current => ({ ...current, season }))} />
                  <p className="self-end text-sm text-muted">{t('exactEditionHint')}</p>
                </div>
              )
            : <p className="text-sm text-muted">{t('multiCompetitionTimeHint')}</p>}
          <datalist id="research-competition-options">
            {facetsQuery.data?.facets.competitionNames.slice(0, 100).map((item: any) => <option key={item.value} value={item.value} />)}
          </datalist>
        </form>
      </Card>

      <AppliedFilters filters={filters} competitionOptions={competitionOptions} />

      <div ref={resultsRef}>
        <Card className="overflow-hidden" variant="secondary">
          <Card.Header>
            <div className="w-full space-y-3">
              <h2 className="text-lg font-bold">{t('resultsTitle')}</h2>
              <p className="text-sm text-muted">{t('resultsSubtitle')}</p>
              {!statsQuery.isPending && !statsQuery.isError && (
                <div className="flex flex-col gap-3 text-sm text-muted md:flex-row md:items-center md:justify-between">
                  <span>{t('resultCount', { matches: matchGroups.length, start: resultStart, end: resultEnd, total: totalRows })}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" isDisabled={!canGoPrev} onPress={() => goToResultOffset(currentOffset - currentLimit)}>{t('previousPage')}</Button>
                    <Button size="sm" variant="outline" isDisabled={!canGoNext} onPress={() => goToResultOffset(currentOffset + currentLimit)}>{t('nextPage')}</Button>
                  </div>
                </div>
              )}
            </div>
          </Card.Header>
          <Card.Content className="p-0">
            {statsQuery.isPending && <div className="p-5 text-sm text-muted">{t('loading')}</div>}
            {statsQuery.isError && <div className="p-5 text-sm text-danger">{t('error')}</div>}
            {!statsQuery.isPending && !statsQuery.isError && (statsQuery.data?.rows.length ?? 0) === 0 && (
              <div className="p-5 text-sm text-muted">{t('empty')}</div>
            )}
            {matchGroups.map(group => <MatchStatCard key={group.key} group={group} />)}
          </Card.Content>
        </Card>
      </div>
    </div>
  )
}

function MatchStatCard({ group }: { group: MatchStatGroup }) {
  const t = useTranslations('ResearchWorkspace.worldcup')
  const locale = useLocale() as ImplementedLocale
  const primary = group.rows[0]
  const paired = group.rows.find(row => row.team === primary.opponent) ?? group.rows.find(row => row.id !== primary.id)
  const homeName = formatTeamName(primary.team, locale)
  const awayName = formatTeamName(primary.opponent, locale)
  const scoreText = `${formatValue(primary.goals)} - ${formatValue(primary.opponentGoals)}`
  const detailLink = primary.matchId ? matchDetailPath(primary.matchId, locale) : null
  const providers = uniqueValues(group.rows.flatMap(row => row.sourceReferences.map((source: any) => source.provider))).filter(provider => provider !== 'api_football').map(formatProviderName)
  const confidence = uniqueValues(group.rows.flatMap(row => Object.values(row.fieldConfidence ?? {}))).filter(value => value !== 'single_source')
  const confidenceLabelByValue = new Map([
    ['trusted', t('confidenceOptions.trusted')],
    ['definition_sensitive', t('confidenceOptions.definitionSensitive')],
    ['provider_specific', t('confidenceOptions.providerSpecific')],
    ['single_source', t('confidenceOptions.singleSource')],
    ['unverified', t('confidenceOptions.unverified')],
    ['rejected', t('confidenceOptions.rejected')],
  ])
  const confidenceBadges = confidence.map(value => confidenceLabelByValue.get(value) ?? value)
  const comparisonMetrics = [
    { key: 'shotsOnTarget', label: t('metrics.shotsOnTarget'), home: primary.shotsOnTarget, away: paired?.shotsOnTarget },
    { key: 'shotsTotal', label: t('metrics.shotsTotal'), home: primary.shotsTotal, away: paired?.shotsTotal },
    { key: 'corners', label: t('metrics.corners'), home: primary.corners, away: paired?.corners },
    { key: 'expectedGoals', label: t('metrics.expectedGoals'), home: primary.expectedGoals, away: paired?.expectedGoals },
  ].filter(metric => (metric.home !== null && metric.home !== undefined) || (metric.away !== null && metric.away !== undefined))
  const content = (
    <>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.8fr)] lg:items-start">
        <div className="space-y-3">
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
            <div className="min-w-0 text-lg font-black tracking-normal md:text-xl">{homeName}</div>
            <div className="rounded-md border border-divider bg-default-100 px-3 py-2 text-center text-xl font-black tabular-nums text-foreground md:min-w-24 md:text-2xl">
              {scoreText}
            </div>
            <div className="min-w-0 text-right text-lg font-black tracking-normal md:text-xl">{awayName}</div>
          </div>
          <div className="text-sm font-medium text-muted">
            {formatCompetitionName(primary.competitionName, locale)}
            {' '}
            ·
            {primary.season}
            {' '}
            ·
            {formatValue(primary.stage)}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {detailLink && (
            <span className="inline-flex min-h-8 items-center rounded-md bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
              {t('openDetail')}
            </span>
          )}
          {[...providers, ...confidenceBadges].map(item => (
            <span key={item} className="rounded-md border border-divider bg-default-100 px-2.5 py-1 text-xs font-semibold text-muted">
              {item}
            </span>
          ))}
        </div>
      </div>

      {comparisonMetrics.length > 0 && (
        <div className="mt-4 space-y-2">
          {comparisonMetrics.map((metric) => {
            const max = Math.max(
              typeof metric.home === 'number' ? metric.home : 0,
              typeof metric.away === 'number' ? metric.away : 0,
            )
            return (
              <div key={metric.key} className="grid grid-cols-[3.5rem_minmax(0,1fr)_5.5rem_minmax(0,1fr)_3.5rem] items-center gap-3 rounded-md border border-divider bg-default-50 px-3 py-2.5 text-sm">
                <div className="text-right font-bold tabular-nums">{formatValue(metric.home)}</div>
                <div className="flex h-2 justify-end rounded-full bg-default-100">
                  <div className="h-2 rounded-full bg-primary/55" style={{ width: metricShare(metric.home, max) }} />
                </div>
                <div className="text-center text-xs font-semibold text-muted">{metric.label}</div>
                <div className="flex h-2 rounded-full bg-default-100">
                  <div className="h-2 rounded-full bg-primary" style={{ width: metricShare(metric.away, max) }} />
                </div>
                <div className="font-bold tabular-nums">{formatValue(metric.away)}</div>
              </div>
            )
          })}
        </div>
      )}
    </>
  )

  const cardClassName = 'block border-t border-divider bg-background/65 p-4 first:border-t-0 sm:p-5'
  const interactiveClassName = `${cardClassName} transition hover:bg-default-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2`

  if (detailLink) {
    return (
      <Link
        to={detailLink.to as any}
        params={detailLink.params as any}
        className={interactiveClassName}
        aria-label={`${homeName} ${scoreText} ${awayName}`}
      >
        {content}
      </Link>
    )
  }

  return (
    <article className={cardClassName} aria-label={`${homeName} ${scoreText} ${awayName}`}>
      {content}
    </article>
  )
}
